'use client';
import React from 'react';

const ApproachSection: React.FC = () => {
  const steps = [
  {
    number: '01',
    title: 'Content Strategy',
    description: 'We begin with a clear editorial strategy — understanding the audience, defining the content scope, and establishing the publishing framework that will guide every issue.'
  },
  {
    number: '02',
    title: 'Editorial Development',
    description: 'Our editorial process ensures every piece of content meets professional standards. We focus on accuracy, relevance, and engaging presentation that resonates with readers.'
  },
  {
    number: '03',
    title: 'Publication & Distribution',
    description: 'We manage the publishing workflow from final content to distribution, ensuring timely delivery across digital channels and platforms that reach your target audience.'
  },
  {
    number: '04',
    title: 'Ongoing Partnership',
    description: 'Publishing is a continuous relationship. We work with clients and partners on an ongoing basis to maintain quality, consistency, and growth in readership and engagement.'
  }];


  const whyUs = [
  {
    icon:
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" stroke="#0066CC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>,

    title: 'Industry Expertise',
    description: 'Deep knowledge of the periodicals and magazine publishing industry.'
  },
  {
    icon:
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" stroke="#0066CC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>,

    title: 'Professional Team',
    description: 'A dedicated team committed to editorial quality and publishing excellence.'
  },
  {
    icon:
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" stroke="#0066CC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>,

    title: 'Florida-Based',
    description: 'Locally based in Jacksonville, FL, serving clients across the region and beyond.'
  },
  {
    icon:
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M13 10V3L4 14h7v7l9-11h-7z" stroke="#0066CC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>,

    title: 'Digital-Forward',
    description: 'Embracing modern publishing platforms and digital distribution channels.'
  },
  {
    icon:
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" stroke="#0066CC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>,

    title: 'Responsive Communication',
    description: 'We prioritize clear, timely communication with every client and partner.'
  },
  {
    icon:
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" stroke="#0066CC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>,

    title: 'Quality Assurance',
    description: 'Rigorous quality standards applied to every publication and content piece.'
  }];


  return (
    <>
      {/* Our Approach Section */}
      <section
        className="py-24"
        style={{ backgroundColor: '#ffffff' }}
        aria-label="Our publishing approach">
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span
              className="inline-block text-sm uppercase tracking-widest font-medium mb-3"
              style={{ fontFamily: 'Poppins, sans-serif', color: '#0066CC', letterSpacing: '3px' }}>
              
              How We Work
            </span>
            <h2
              className="mb-6"
              style={{
                fontFamily: 'Roboto, sans-serif',
                fontWeight: 700,
                fontSize: 'clamp(28px, 3.5vw, 42px)',
                color: '#122658',
                lineHeight: 1.2
              }}>
              
              Our Approach to Publishing
            </h2>
            <div className="w-16 h-1 mx-auto" style={{ backgroundColor: '#387EF7' }} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) =>
            <div key={index} className="relative">
                {/* Connector line */}
                {index < steps.length - 1 &&
              <div
                className="hidden lg:block absolute top-8 left-full w-full h-0.5 z-0"
                style={{ backgroundColor: '#E5E7EB', width: 'calc(100% - 2rem)' }} />

              }
                <div className="relative z-10">
                  <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mb-6 font-roboto font-bold text-xl"
                  style={{
                    backgroundColor: '#122658',
                    color: '#ffffff',
                    fontFamily: 'Roboto, sans-serif',
                    fontWeight: 700,
                    fontSize: '18px'
                  }}>
                  
                    {step.number}
                  </div>
                  <h3
                  className="mb-4"
                  style={{
                    fontFamily: 'Roboto, sans-serif',
                    fontWeight: 700,
                    fontSize: '18px',
                    color: '#122658'
                  }}>
                  
                    {step.title}
                  </h3>
                  <p
                  style={{
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '14px',
                    color: '#7F7F90',
                    lineHeight: 1.7
                  }}>
                  
                    {step.description}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Why Work With Us */}
      <section
        className="py-24"
        style={{ backgroundColor: '#F2F3F3' }}
        aria-label="Why work with Caps N Corks II">
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div>
              <span
                className="inline-block text-sm uppercase tracking-widest font-medium mb-3"
                style={{ fontFamily: 'Poppins, sans-serif', color: '#0066CC', letterSpacing: '3px' }}>
                
                Why Choose Us
              </span>
              <h2
                className="mb-6"
                style={{
                  fontFamily: 'Roboto, sans-serif',
                  fontWeight: 700,
                  fontSize: 'clamp(26px, 3vw, 38px)',
                  color: '#122658',
                  lineHeight: 1.2
                }}>
                
                Your Search for the Right Publishing Partner Ends Here
              </h2>
              <div className="w-16 h-1 mb-6" style={{ backgroundColor: '#387EF7' }} />
              <p
                className="mb-8"
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '15px',
                  color: '#7F7F90',
                  lineHeight: 1.8
                }}>
                
                Caps N Corks II, LLC brings a focused, professional approach to magazine and periodical publishing. We understand the demands of the publishing industry and are committed to delivering quality, consistency, and results for every client and partner we work with.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {whyUs.map((item, index) =>
                <div key={index} className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">{item.icon}</div>
                    <div>
                      <h4
                      className="mb-1"
                      style={{
                        fontFamily: 'Roboto, sans-serif',
                        fontWeight: 700,
                        fontSize: '14px',
                        color: '#122658'
                      }}>
                      
                        {item.title}
                      </h4>
                      <p
                      style={{
                        fontFamily: 'Poppins, sans-serif',
                        fontSize: '13px',
                        color: '#7F7F90',
                        lineHeight: 1.6
                      }}>
                      
                        {item.description}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Image */}
            <div className="relative">
              <div
                className="rounded-lg overflow-hidden"
                style={{ boxShadow: '4px 8px 30px rgba(18,38,88,0.15)' }}>
                
                <img
                  src="https://images.unsplash.com/photo-1457369804613-52c61a468e7d?w=800&q=80"
                  alt="Stack of professional magazines and periodicals representing quality publishing"
                  className="w-full h-96 object-cover" />
                
              </div>
              <div
                className="absolute -top-6 -left-6 w-32 h-32 rounded-lg z-0 hidden lg:block"
                style={{ backgroundColor: '#0066CC', opacity: 0.1 }} />
              
            </div>
          </div>
        </div>
      </section>
    </>);

};

export default ApproachSection;